import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IProduct } from '../../../../core/models/product.model';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import domtoimage from 'dom-to-image-more';
import PptxGenJS from 'pptxgenjs';
import _ from 'lodash';
import jsPDF, { jsPDFOptions } from 'jspdf';
import { IDisplayMetric } from '../../../../core/models/display-metric.model';
import { NotificationService } from '../../../../core/core.module';

@Component({
    selector: 'lynkd-pattern-gallery-export',
    templateUrl: './gallery-export.component.html',
    styleUrls: ['./gallery-export.component.scss']
})
export class GalleryExportComponent implements OnInit {
    public galleryItems: Array<IProduct & { image_url: SafeUrl }>;
    public galleryItemsPages: Array<Array<IProduct & { image_url: SafeUrl }>>;
    public exportCap: number = 60;
    public isExporting: boolean = false;
    public layoutConfigs: Array<{
        layout: string;
        pageSize: number;
    }> = [
        {
            pageSize: 18,
            layout: '6x3'
        },
        {
            pageSize: 6,
            layout: '3x2'
        },
        {
            pageSize: 8,
            layout: '4x2'
        }
    ];
    public selectedConfig: {
        layout: string;
        pageSize: number;
    };
    public pageSizes: Array<{ name: string; width?: number; height?: number }> = [
        {
            name: 'A3',
            width: 16.5,
            height: 11.7
        },
        {
            name: 'A4',
            width: 11.7,
            height: 8.3
        }
    ];

    public fileHeader: string;
    public fileSubHeader: string;
    public exportType: string;
    public filename: string;
    public availableMetrics: Array<string>;
    public selectedPageSize: { name: string; width?: number; height?: number };

    @ViewChild('gallery')
    private readonly gallery: ElementRef<HTMLElement>;

    public constructor(
        @Inject(MAT_DIALOG_DATA) public readonly data: { products: Array<IProduct>; filterData: Array<IDisplayMetric>; metricAlignment: boolean },
        private readonly _dialogRef: MatDialogRef<GalleryExportComponent>,
        private readonly _notificationService: NotificationService,
        private readonly _sanitizer: DomSanitizer
    ) {
    }

    public ngOnInit(): void {
        this.setInitialLayoutConfig();
        this.initializeGallery();
    }

    public setGalleryLayout(): void {
        this.initializeGallery();
    }

    public getLayout(): string {
        if (this.selectedConfig.layout === '6x3') {
            return `gallery-item-6x3`;
        } else if (this.selectedConfig.layout === '3x2') {
            return `gallery-item-3x2`;
        } else if (this.selectedConfig.layout === '4x2') {
            return `gallery-item-4x2`;
        } else {
            return `gallery-item-6x3`;
        }
    }

    public getMetricStyle(): Record<string, string> {
        if (this.data.metricAlignment === false) {
            return { bottom: '0', top: 'unset' };
        } else {
            return { top: '0', bottom: 'unset' };
        }
    }

    public async exportAsPdf(): Promise<void> {
        this.isExporting = true;

        const options: jsPDFOptions = {
            orientation: 'l',
            unit: 'in',
            format: `${this.selectedPageSize.name.toLowerCase()}`
        };
        const pdf: jsPDF = new jsPDF(options);

        for (const [index, page] of this.galleryItemsPages.entries()) {
            try {
                if (index !== 0) {
                    pdf.addPage(`${this.selectedPageSize.name.toLowerCase()}`, 'l');
                }

                this.galleryItems = [...page];

                // Ensure that all pending DOM changes are applied before proceeding
                await new Promise((resolve: (value: unknown) => void) => requestAnimationFrame(resolve));

                // Create a slight delay to gives the browser extra time to fully render
                await new Promise((resolve: (value: unknown) => void) => setTimeout(resolve, 100));

                const galleryDOM: HTMLElement = document.querySelector('.gallery-export');

                // Handle potential CORS issues by replacing problematic images
                await this.handleCorsImages(galleryDOM);

                try {
                    const dataUrl: string = await domtoimage.toJpeg(galleryDOM);
                    pdf.addImage(dataUrl, 'JPEG', 0, 0, this.selectedPageSize.width, this.selectedPageSize.height);
                } catch (error) {
                    this._notificationService.warn("One page could not be rendered completely.");
                    continue; // Skip to next page if the entire page fails
                }
            } catch (error) {
                this._notificationService.warn("Error processing one page, continuing with others.");
                continue;
            }
        }

        pdf.save(`${this.filename}.pdf`);
        this.isExporting = false;
        // this._dialogRef.close();
    }

    public async handleCorsImages(element: HTMLElement): Promise<void> {
        const images: Array<HTMLImageElement> = Array.from(element.getElementsByTagName('img'));
        
        await Promise.all(images.map(async (img: HTMLImageElement) => {
            // Skip if already complete and not errored
            if (img.complete && !img.naturalHeight) return;

            try {
                // Create a test image to check for CORS issues
                const testImg: HTMLImageElement = new Image();
                testImg.crossOrigin = 'Anonymous';
                testImg.src = img.src;
                
                // Wait for load or error
                await new Promise((resolve: (value: unknown) => void) => {
                    testImg.onload = resolve;
                    testImg.onerror = (): void => {
                        // Replace the problematic image with a blank placeholder
                        img.src = 'data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" width="' + 
                                img.width + '" height="' + img.height + 
                                '" viewBox="0 0 ' + img.width + ' ' + img.height + 
                                '"><rect width="100%" height="100%" fill="lightgray"/></svg>';
                        resolve(null);
                    };
                });
            } catch (error) {
                // Fallback to blank image if the check itself fails
                img.src = 'data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" width="' + 
                            img.width + '" height="' + img.height + 
                            '" viewBox="0 0 ' + img.width + ' ' + img.height + 
                            '"><rect width="100%" height="100%" fill="lightgray"/></svg>';
            }
        }));
    }

    public async waitForImagesToLoad(element: HTMLElement): Promise<void> {
        const images: Array<HTMLImageElement> = Array.from(element.getElementsByTagName('img'));
        await Promise.all(images.map(async (img: HTMLImageElement) => 
            new Promise((resolve: (value: unknown) => void) => 
                                img.complete ? resolve(null) : img.addEventListener('load', () => resolve(null)))
        ));
    }

    public async exportAsPowerpoint(): Promise<void> {
        this.isExporting = true;
        const xOrigin: number = 0;
        const yOrigin: number = 0;
        const pptx: PptxGenJS = new PptxGenJS();

        pptx.defineLayout({ name: 'A3', width: 16.5, height: 11.7 });
        pptx.defineLayout({ name: 'A4', width: 11.7, height: 8.3 });
        pptx.layout = this.selectedPageSize.name;

        for (const [index, page] of this.galleryItemsPages.entries()) {
            try {
                this.galleryItems = [...page];

                // Ensure that all pending DOM changes are applied before proceeding
                await new Promise((resolve: (value: unknown) => void) => requestAnimationFrame(resolve));

                // Create a slight delay to gives the browser extra time to fully render
                await new Promise((resolve: (value: unknown) => void) => setTimeout(resolve, 200));

                const galleryElement: HTMLElement = this.gallery.nativeElement;

                // Handle potential CORS issues by replacing problematic images
                await this.handleCorsImages(galleryElement);

                try {
                    const dataUrl: string = await domtoimage.toPng(galleryElement);
                    const slide: PptxGenJS.Slide = pptx.addSlide();
                    slide.addImage({
                        path: dataUrl,
                        x: xOrigin,
                        y: yOrigin,
                        w: '100%',
                        h: '100%',
                        sizing: {
                            type: 'cover',
                            w: '100%',
                            h: '100%'
                        }
                    });
                } catch (error) {
                    this._notificationService.warn("One slide could not be rendered completely.");
                    continue; // Skip to next slide if this one fails
                }
            } catch (error) {
                this._notificationService.warn("Error processing one slide, continuing with others.");
                continue;
            }
        }

        try {
            await pptx.writeFile({ fileName: `${this.filename}.pptx` });
        } catch (error) {
            this._notificationService.error("Failed to save PowerPoint file.");
        } finally {
            this.isExporting = false;
            this._dialogRef.close();
        }
    }

    public close(): void {
        this._dialogRef.close();
    }

    private setInitialLayoutConfig(): void {
        this.selectedConfig = this.layoutConfigs.filter(
            (config: { layout: string; pageSize: number }) => config.layout === '6x3'
        )[0];
    }

    private initializeGallery(): void {
        this.galleryItemsPages = _.chunk(
            this.data.products.slice(0, this.exportCap).map((item: IProduct & { imageId: string }) => ({
                ...item,
                image_url: this._sanitizer.bypassSecurityTrustUrl(item.imageId)
            })),
            this.selectedConfig.pageSize
        );

        const page: Array<IProduct & { image_url: SafeUrl }> = this.galleryItemsPages[this.galleryItemsPages.length -1];
        let remainingImageSlots: number = this.selectedConfig.pageSize - page.length;
        while (remainingImageSlots > 0) {
            page.push(undefined);
            remainingImageSlots--;
        }

        this.galleryItems = this.galleryItemsPages[0];
        this.availableMetrics = [...Object.keys(this.galleryItems[0])];
    }
}
